import { forwardRef } from 'react';

import { BotaoFechar } from '@/componentes/Botao';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Tabela, type Coluna } from '@/componentes/Tabela';

import { comoResolverARevisao, motivoDaRevisao, type MaquinaARevisar } from './tipos';

/**
 * REVISAR TIPOS (0131, decisão 57). Pedido do dono (29/10/2026): "sei que temos
 * atualmente máquinas com tipos que são incorretos; somente me pontue quais
 * casos devo corrigir". O sistema não corrige sozinho: aponta.
 *
 * O aviso é o BANNER da p.33 (atenção, com o número), registrado só enquanto
 * `/equipamentos/a-revisar` tiver máquina. "Revisar tipos" abre a lista, e
 * cada linha abre a máquina no painel, onde a ficha troca o tipo. Corrigir o
 * tipo tira a máquina da lista sozinho: a consulta mora sob `['frota']`, que
 * todo ajuste da ficha renova. O banner mora em `banner-da-revisao.ts`.
 */
const COLUNAS: readonly Coluna<MaquinaARevisar>[] = [
  {
    chave: 'codigo',
    rotulo: 'Código',
    celula: (m) => <span className="codigo whitespace-nowrap text-tinta">{m.codigo}</span>,
  },
  {
    chave: 'tipo',
    rotulo: 'Tipo atual',
    celula: (m) => (m.tipo ? <span>{m.tipo}</span> : <span className="text-tinta-secundaria">Sem tipo</span>),
  },
  {
    chave: 'motivo',
    rotulo: 'O que está errado',
    celula: (m) => (
      <span className="flex flex-col">
        <span className="text-tinta">{motivoDaRevisao(m)}</span>
        <span className="txt-apoio text-tinta-secundaria">{comoResolverARevisao(m)}</span>
      </span>
    ),
  },
];

/** A lista das máquinas a revisar, num cartão da página da frota. */
export const ListaDaRevisao = forwardRef<
  HTMLElement,
  {
    readonly maquinas: readonly MaquinaARevisar[];
    /** Quem não gere a frota vê a lista, mas quem troca o tipo é o Administrador. */
    readonly podeGerir: boolean;
    readonly aoAbrir: (id: string) => void;
    readonly aoFechar: () => void;
  }
>(function ListaDaRevisao({ maquinas, podeGerir, aoAbrir, aoFechar }, ref) {
  return (
    <section ref={ref} tabIndex={-1} aria-labelledby="revisar-tipos-titulo" className="cartao flex flex-col gap-4 p-6 outline-none">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 id="revisar-tipos-titulo" className="txt-subtitulo text-tinta">
            Revisar tipos
          </h2>
          <p className="txt-apoio text-tinta-secundaria">
            {podeGerir
              ? 'Abra a máquina e troque o tipo na ficha. O código não muda; a máquina sai desta lista quando o tipo estiver certo.'
              : 'Quem troca o tipo é o perfil Administrador. Abra a máquina para conferir a ficha.'}
          </p>
        </div>
        <BotaoFechar rotulo="Fechar a lista de tipos para revisar" aoFechar={aoFechar} />
      </div>
      <Tabela
        rotulo="Máquinas com o tipo para revisar"
        colunas={COLUNAS}
        linhas={maquinas}
        alturaDupla
        chaveDaLinha={(m) => m.id}
        aoSelecionar={(m) => {
          aoAbrir(m.id);
        }}
        vazio={
          <EstadoVazio
            titulo="Nenhuma máquina para revisar"
            descricao="Todos os tipos estão na lista e combinam com o código. Feche esta lista para voltar à frota."
          />
        }
      />
    </section>
  );
});
